import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { SubHeader, SubHeaderMenu } from '@app/components/Skeleton/Skeleton';
import useLocaleWithFallback from '@app/hooks/useLocaleWithFallback';
import useLocationSearch from '@app/hooks/useLocationSearch';
import { DIRECTUS_SUPER_CLIENT_URL_KEY } from '@config/common.config';

import type {
  PricingPageHeaderData,
  PricingPageHeaderDataVariables,
} from './__generated__/PricingPageHeaderData';
import PricesSection from './PricesSection';
import { PRICING_PAGE_HEADER_DATA } from './PricingPageHeader.queries';
import Title from './Title';

const PricingPageHeader: FC<{
  sku?: string;
  showPrices?: boolean;
}> = ({ sku, showPrices }) => {
  const { url_key } = useParams<{
    url_key: string;
  }>();

  const directusSuperClientToken = useLocationSearch(
    DIRECTUS_SUPER_CLIENT_URL_KEY,
  );
  const { getLanguagesWithFallback, findTranslationByKeyWithFallback } =
    useLocaleWithFallback();

  const { data, loading, error } = useQuery<
    PricingPageHeaderData,
    PricingPageHeaderDataVariables
  >(PRICING_PAGE_HEADER_DATA, {
    variables: {
      languages: getLanguagesWithFallback(),
      url_key,
    },
    context: {
      directusSuperClientToken,
    },
    fetchPolicy: 'network-only',
  });

  if (loading) return null;
  if (error) {
    throw new Error(`Unable to query PricingPageHeaderData: ${error}`);
  }

  const pricingPageData = data?.content?.pricing_pages?.[0];

  if (!pricingPageData) return null;

  const { layout, title, title_color } = pricingPageData;
  const layoutItem = layout?.[0]?.item;

  if (!layoutItem) {
    throw new Error('No layout is specified for this page');
  }

  const isSingleLayout =
    layoutItem?.__typename === 'pricing_pages_single_layout';

  const links = isSingleLayout ? layoutItem?.product?.links : layoutItem?.links;

  const headerLinks = links?.map((link) => {
    const translations = isSingleLayout
      ? link?.products_links_id?.translations
      : link?.translations;
    return {
      title: findTranslationByKeyWithFallback({
        translations,
        key: 'title',
      }),
      url: findTranslationByKeyWithFallback({
        translations,
        key: 'url',
      }),
    };
  });

  return (
    <SubHeader bgColor={title_color}>
      {sku && title && <Title sku={sku} title={title} />}

      <SubHeaderMenu>
        {headerLinks?.map(
          (link, index) =>
            link?.title &&
            link?.url && (
              <a
                key={index}
                href={link?.url}
                data-testid={`pricing-subheader-link-${index}`}
              >
                {link?.title}
              </a>
            ),
        )}
      </SubHeaderMenu>

      {showPrices && sku && <PricesSection sku={sku} />}
    </SubHeader>
  );
};

export default PricingPageHeader;
